import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { unstable_batchedUpdates as batch } from 'react-dom';
import { useSelector } from 'react-redux';
import agentService from '../../_services/agent.service';
import NotificationBell from '../notifications/NotificationBell';
import LocationPicker from '../multiLocation/LocationPicker';
import MetricsCard from './MetricsCard';
import BuilderCard from './BuilderCard';
import DraftCard from './DraftCard';
import DraftWidget from './bailey/skills/widgets/DraftWidget';
import { ThinkingWord } from './bailey/thinking';
import { letteredAnswer } from './bailey/skills/widgets/ChoicesWidget';
import { campaignDraftsOf, draftCardHomes, showsDraftCardAt, messagesFromTranscript, draftsToOpen } from './draftCards';
import DiaryCard from './DiaryCard';
import ApprovalCard, { readApproval, approvalErrorOf } from './ApprovalCard';
import { saveSchemeAsync, loadScheme, hydrateLoyalty } from '../loyalty-v2/loyaltyStore';
import BaileyAvatar, { BaileyStill } from './bailey/BaileyAvatar';
import BaileyWardrobe from './bailey/BaileyWardrobe';
import { getBaileyActivity } from './bailey/activity';
import { publishRun, previewOf, useBaileyRun } from './bailey/baileyRun';
import { openProposal, describeProposal } from './bailey/baileyBooking';
import { normaliseAppearance } from './bailey/appearance';
import { resolveAppearance, serverHasExtras, extrasAvailable, savePayload, afterSave, rememberLook } from './bailey/lookStore';
import { getBaileyState, STATE_LABELS } from './bailey/looks';
import { ideasFor } from './bailey/ideas';
import { loadIdeas, followUpsFor } from './bailey/demo';
import { threadTitle } from './bailey/format';
import { useSetupPill } from '../onboarding/setup/pillStore';
import { readLocalPins, serverPins, canPin as pinsAllowed, setPinned } from './bailey/pins';
import { hapticError, hapticSuccess, hapticTick } from '../../_utils/haptics';
import BaileyHome from './BaileyHome';
import { Composer, Icon, MoreMenu, WorkSteps } from './BaileyBits';
import { ConversationDock, ConversationDrawer, ConversationPeek, HowIWorkPanel, useConversations } from './BaileyPanels';
import useFrontDesk from './frontdesk/useFrontDesk';
import FrontDeskPage from './frontdesk/FrontDeskPage';
import ConversationsPage from './frontdesk/ConversationsPage';
import FrontDeskSetup from './frontdesk/FrontDeskSetup';
import SoulPage from './frontdesk/SoulPage';
import { CALL_TOOL, isCall } from './frontdesk/liveCall';
import { CONCIERGE_VISIBLE, PRICING_V2_VISIBLE } from '../../config/featureFlags';
import usePricingUsage, { bumpBaileyUsed, markBaileyOut } from '../pricing/usageStore';
import { quotaRefusal } from '../pricing/usageRules';
import BaileyLocked from './BaileyLocked';
import { BaileyQuotaCard, BaileyQuotaLine } from '../pricing/BaileyQuota';
import { isOwnerHere } from '../permissions/access';
import { getUserRole } from '../settings/useSettingsTabs';
import BaileyReply from './bailey/skills/BaileyReply';
import { messageFor, markBriefed } from './bailey/skills/context';
import { yesSaves, hasSavers, saveLatest, savedLine, isChangeIntent, hasEditors, editLatest, changedLine, screenFacts } from './bailey/skills/saveBus';
import { stripContext, readyLine, splitReply } from './bailey/skills/protocol';
import { asksToTap, tidyWords } from './bailey/skills/tidy';
import './agent.css';
import './bailey-tab.css';
import './frontdesk/frontdesk.css';

// What they said in the turns before reply i (their last three messages), so
// what Bailey shows can fit their own words
const askedBefore = (shown, i) => shown.slice(0, i).filter(x => x.role === 'me').slice(-3).map(x => x.text).join('\n')

/**
 * The Bailey tab.
 *
 * DESIGN RULES (docs/agent-p0.md §6), because the audience is salon owners and
 * barbers rather than software people:
 *
 *  - Renders NOTHING until /status says this account may use it. The
 *    concierge's first test hit a greet-then-refuse bubble and was rightly
 *    roasted; a dead button is worse than no button.
 *  - Every answer ends in something tappable. No dead ends.
 *  - Anything needing a decision is a CARD with buttons, never a sentence
 *    asking an open question.
 *  - An approval shows the number before the tap, in its own block, because
 *    the person is agreeing to a cost and it must not hide inside prose.
 *  - The positive action sits on the right of every button row.
 *
 * Two views: HOME (BaileyHome: what's waiting, what's in progress, ideas) and
 * a CONVERSATION. The page sits under the app's own TopHeader like every
 * other page, so the menu is always there to leave by.
 */

function SetupCard({ items }) {
    // Lead with what is missing. A checklist of green ticks is the form again.
    const missing = items.filter(i => i.done === false);
    const unknown = items.filter(i => i.done === null || i.done === undefined);
    const shown = missing.length ? missing : items;

    return (
        <div className="agt-card">
            <h4 className="agt-card-title">
                {missing.length
                    ? `${missing.length} thing${missing.length > 1 ? 's' : ''} to sort`
                    : 'You’re all set up'}
            </h4>
            {shown.map(item => (
                <div className="agt-gap" key={item.key}>
                    <span className={
                        'agt-gap-dot ' +
                        (item.done === true ? 'is-done'
                            : item.done === false ? 'is-missing' : 'is-unknown')
                    } />
                    <span className="agt-gap-text">
                        {item.detail || item.key}
                        {item.blocks && item.done === false && (
                            <><br /><span className="agt-gap-sub">Needed for {item.blocks}.</span></>
                        )}
                    </span>
                </div>
            ))}
            {!!unknown.length && !missing.length && (
                <p className="agt-card-body" style={{ marginTop: 10, marginBottom: 0 }}>
                    {/* Unknown is NOT "not done". Saying so would be a lie the
                        person then acts on. */}
                    Couldn’t check {unknown.length} of these just now.
                </p>
            )}
        </div>
    );
}

/**
 * A booking Bailey got ready. Nothing is booked: the button opens the diary's
 * own new-booking panel filled in, and they save it there.
 */
function BookingCard({ card, onOpen }) {
    const who = card.client && card.client.name;
    const what = (card.services || []).map(s => s.serviceName).join(', ');
    const detail = card.type === 'booking'
        ? [who || 'No client yet', what || 'No service yet'].join(' · ')
        : card.type === 'leave'
            ? ({ holiday: 'Holiday', sick: 'Sick', other: 'Time off' }[card.leaveType] || 'Time off') + (card.title ? ` · ${card.title}` : '')
            : (card.isBookingAllowed ? 'Clients can still book over it' : 'Not bookable');
    return (
        <div className="agt-card agt-booking">
            <p className="bly-eyebrow">Ready to save</p>
            <p className="agt-booking-when">{describeProposal(card)}</p>
            <p className="agt-booking-what">{detail}</p>
            {(card.warnings || []).concat(card.notes || []).map((w, i) => (
                <p key={i} className="agt-booking-warn">{w}</p>
            ))}
            <div className="bly-actions">
                <button type="button" className="bly-btn is-primary" onClick={() => { hapticTick(); onOpen(); }}>Open in the diary</button>
            </div>
        </div>
    );
}

const STORAGE_KEY = 'agentSessionId';

/**
 * Where the conversations list goes. It always comes from the left. It docks
 * beside the thread (pushing it over) only where the app's own side nav is
 * showing (1100px and up) and there's room. Below 1100px every screen in the
 * app has the menu button in its top-left corner, so the list slides over the
 * page instead of docking in front of that button.
 */
/**
 * Hover-to-peek for the hidden list: a short pause before it opens, so a
 * pointer passing over the icon doesn't flash it, and a grace period before
 * it closes, so moving from the icon onto the list keeps it. Mouse and
 * trackpad only; on touch a tap opens the list as before.
 */
function usePeek(enabled) {
    const [open, setOpen] = useState(false);
    const timer = useRef(null);
    const fine = useRef(false);
    useEffect(() => {
        try { fine.current = window.matchMedia('(hover: hover) and (pointer: fine)').matches; } catch (e) { fine.current = false; }
        return () => clearTimeout(timer.current);
    }, []);
    useEffect(() => { if (!enabled) setOpen(false); }, [enabled]);
    const later = (fn, ms) => { clearTimeout(timer.current); timer.current = setTimeout(fn, ms); };
    return {
        open: open && enabled,
        // 300ms: long enough that passing over the icon on the way elsewhere
        // doesn't open it, short enough that resting on it feels immediate.
        enter: () => { if (fine.current && enabled) later(() => setOpen(true), 300); },
        stay: () => clearTimeout(timer.current),
        leave: () => later(() => setOpen(false), 280),
        close: () => { clearTimeout(timer.current); setOpen(false); }
    };
}

const DOCK_MIN = 840;       // content width needed to dock at all
const DOCK_DEFAULT = 1000;  // and to start docked open, before they choose
function drawerLayout({ vw, navFolded, sidePref, drawerOpen }) {
    const room = vw - (navFolded ? 72 : 250);
    const dock = vw >= 1100 && room >= DOCK_MIN;
    const visible = dock ? (sidePref ? sidePref === 'open' : room >= DOCK_DEFAULT) : drawerOpen;
    return { dock, visible };
}
// Coming back within this long (say from a quick look at the diary) reopens
// the conversation; after that, the Bailey home, where anything waiting shows.
const RESUME_MS = 10 * 60 * 1000;
const remember = (id) => {
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ id, at: Date.now() })); } catch (e) { /* private mode */ }
};
const recall = () => {
    try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const v = raw.charAt(0) === '{' ? JSON.parse(raw) : { id: raw, at: 0 };
        return v && v.id && Date.now() - (v.at || 0) < RESUME_MS ? v.id : null;
    } catch (e) { return null; }
};

/**
 * A reply still on its way, for the home screen: one asked here, or one asked
 * of the desk pet on another page (then "Reply ready" for a few minutes).
 * Kept outside the component, which is already large.
 */
function liveFor({ sending, hasThread, title, petRun, sessionId, petSeenAt }) {
    if (sending && hasThread) {
        return { kind: 'here', busy: true, text: `Bailey is still working on “${title || 'your last question'}”` };
    }
    const run = petRun || {};
    // A brand-new question has no session yet, so it can't be this thread.
    const away = run.source === 'pet' && !sending && run.title && run.at > petSeenAt
        && (!run.sessionId || String(run.sessionId) !== String(sessionId || ''));
    if (!away) return null;
    const name = threadTitle({ title: run.title });
    if (run.phase === 'working') return { kind: 'pet', busy: true, sessionId: run.sessionId, text: `Bailey is working on “${name}”` };
    if (run.phase === 'done' && run.sessionId && Date.now() - run.at < 5 * 60 * 1000) {
        return { kind: 'pet', busy: false, sessionId: run.sessionId, text: `Reply ready: “${name}”` };
    }
    return null;
}
// Bailey's month from Plan & usage, if it's loaded (kept out of the component
// body: every branch there counts against the hooks linter's path limit).
const baileyOf = (usage) => (PRICING_V2_VISIBLE && usage ? usage.bailey : null);

const doneSteps = (steps) => (steps || []).map(s => (s.done ? s : { ...s, done: true }));

/**
 * The front desk's pages when the owner-side Bailey isn't switched on: the
 * same header (back and a title) around the page, and nothing else.
 */
function FrontDeskShell({ title, onBack, style, children }) {
    return (
        <div className="body_container agt-page bly-page">
            <div className="bly" style={style}>
                <div className="bly-main">
                    <header className="bly-head">
                        <button type="button" className="bly-app-menu is-back" onClick={() => { hapticTick(); onBack(); }} aria-label="Back">
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6" /></svg>
                        </button>
                        <div className="bly-head-who"><div className="bly-head-copy"><b>{title}</b></div></div>
                        <div className="bly-head-right"><LocationPicker /><span className="bly-bell"><NotificationBell /></span></div>
                    </header>
                    {children}
                </div>
            </div>
        </div>
    );
}

/** Which of the tab's own pages the address names, if any (frontdesk/). */
const PAGES = { '/assistant/front-desk': 'front-desk', '/assistant/front-desk/setup': 'setup', '/assistant/front-desk/conversations': 'conversations', '/assistant/soul': 'soul' };
const PAGE_TITLES = { 'front-desk': 'Front desk', setup: 'Front desk', conversations: 'Conversations', soul: 'Soul' };
function pageFor(pathname) { return PAGES[String(pathname || '').replace(/\/+$/, '')] || null; }

// Where each of the tab's places was scrolled to, so coming back returns there.
const scrollMemory = new Map();

/**
 * A place's own scroll area (the home, the front desk, its conversations,
 * the soul). Keyed by place, so a place you open starts at the top instead
 * of wherever the last one was left; coming back (back, or home from a
 * conversation) returns to where you were.
 */
function PlaceScroll({ place, restore, children }) {
    const ref = useRef(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        const want = restore ? (scrollMemory.get(place) || 0) : 0;
        el.scrollTop = want;
        // A place still filling in can be too short at first: try again for a
        // moment, until it fits or they scroll themselves.
        let tries = 0;
        let raf = 0;
        let touched = false;
        const again = () => {
            if (touched || !want || Math.abs(el.scrollTop - want) < 2 || tries > 20) return;
            tries += 1;
            el.scrollTop = want;
            raf = requestAnimationFrame(again);
        };
        raf = requestAnimationFrame(again);
        const save = () => scrollMemory.set(place, el.scrollTop);
        const mine = () => { touched = true; };
        el.addEventListener('scroll', save, { passive: true });
        el.addEventListener('wheel', mine, { passive: true });
        el.addEventListener('touchstart', mine, { passive: true });
        return () => {
            cancelAnimationFrame(raf);
            save();
            el.removeEventListener('scroll', save);
            el.removeEventListener('wheel', mine);
            el.removeEventListener('touchstart', mine);
        };
    }, [place]); // eslint-disable-line react-hooks/exhaustive-deps
    return <div ref={ref} className="bly-scroll is-home">{children}</div>;
}
/** The front desk is the salon's: owners and self-employed stylists, never at the reception device. */
function frontDeskAllowed(status, role) {
    if (!CONCIERGE_VISIBLE || !status) return false;
    try { if ((JSON.parse(localStorage.getItem('receptionModeSettings') || 'null') || {}).enabled) return false; } catch (e) { /* not at the desk */ }
    if (status.principalType) return status.principalType !== 'employed-staff';
    // No word from the server (a failed status read): only the account
    // holders it could be, from the signed-in profile.
    return role === 'owner' || role === 'selfEmployed';
}

// What each read scope lets Bailey look at, in plain words.
const SCOPE_WORDS = {
    'salon:read': 'your business details',
    'diary:read': 'your diary',
    'clients:read': 'your clients',
    'finance:read': 'your takings',
    'marketing:read': 'your marketing',
    'loyalty:read': 'your loyalty scheme',
    'brand:read': 'your brand',
};

/** Shown to someone who hasn't switched Bailey on: what it is, what it sees, one button. */
function BaileyOptIn({ status, onEnabled, frontDesk = false, onFrontDesk }) {
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const sees = (status.scopes || []).map((s) => SCOPE_WORDS[s]).filter(Boolean);
    const seesLine = sees.length > 1 ? `${sees.slice(0, -1).join(', ')} and ${sees[sees.length - 1]}` : (sees[0] || 'what you can already see in Beu');
    const switchOn = async () => {
        setBusy(true); setErr('');
        try {
            const r = await agentService.setEnabled(true);
            const d = (r && r.data && r.data.responseData) || {};
            if (d.enabled !== true) throw new Error('not enabled');
            hapticSuccess();
            onEnabled();
        } catch (e) {
            hapticError();
            setErr('I couldn’t switch on just now. Try again in a moment.');
            setBusy(false);
        }
    };
    return (
        <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: '40px 20px' }}>
            <div style={{ maxWidth: 440, width: '100%', textAlign: 'center' }}>
                <div style={{ width: 96, height: 96, margin: '0 auto 18px', borderRadius: '50%', background: '#F1EFEA', overflow: 'hidden', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                    <BaileyStill appearance={status.appearance} />
                </div>
                <h1 style={{ fontSize: 24, fontWeight: 700, margin: '0 0 10px', color: '#29282D' }}>Meet Bailey</h1>
                <p style={{ fontSize: 15, lineHeight: 1.5, color: '#4B4B52', margin: '0 0 10px' }}>
                    Ask about {seesLine}, and get help with the jobs that take your time.
                </p>
                <p style={{ fontSize: 13, lineHeight: 1.5, color: '#7A7A80', margin: '0 0 22px' }}>
                    Bailey only sees what you can already see in Beu. Anything sent to clients, or that costs money, waits for you to approve it. You can switch it off again at any time.
                </p>
                <button type="button" onClick={switchOn} disabled={busy}
                    style={{ padding: '12px 22px', borderRadius: 999, border: 'none', background: '#29282D', color: '#fff', fontSize: 15, fontWeight: 600, cursor: busy ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                    {busy ? 'Switching on…' : 'Switch on Bailey'}
                </button>
                {err && <p role="alert" style={{ color: '#B14438', fontSize: 13, margin: '14px 0 0' }}>{err}</p>}
                {frontDesk && (
                    // The front desk doesn't need this switched on: it's the salon's, not the owner's.
                    <p style={{ fontSize: 14, lineHeight: 1.5, color: '#4B4B52', margin: '22px 0 0' }}>
                        Only want me answering your clients?{' '}
                        <button type="button" onClick={onFrontDesk} style={{ border: 0, background: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: '#006A87', textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                            Put me on your front desk
                        </button>
                    </p>
                )}
            </div>
        </div>
    );
}

export default function AgentThread(props) {
    const [ready, setReady] = useState(null);   // null = still asking
    const [status, setStatus] = useState(null);
    const [messages, setMessages] = useState([]);
    // For send (which doesn't re-make on every message): what Bailey last said
    const messagesRef = useRef(messages);
    messagesRef.current = messages;
    const [sessionId, setSessionId] = useState(null);
    const [title, setTitle] = useState('');
    const [view, setView] = useState('home');   // 'home' | 'thread'
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [busyApproval, setBusyApproval] = useState(null);
    const [error, setError] = useState(null);
    // Bailey's free questions this month (pricing, Oct 2026): the counter
    // from half used, and a card instead of an error when they run out.
    const { usage: pricing } = usePricingUsage({ enabled: PRICING_V2_VISIBLE });
    const [waiting, setWaiting] = useState([]);   // approvals pending right now
    const convos = useConversations();            // earlier threads
    const recent = convos.rows;
    const [goals, setGoals] = useState([]);       // what they're working towards
    // Set while a thread belongs to one goal, so the turn is tagged with it.
    const [threadGoalId, setThreadGoalId] = useState(null);
    const [drafts, setDrafts] = useState([]);     // made, not sent
    const [noticed, setNoticed] = useState([]);
    const [panel, setPanel] = useState(null);     // 'conversations' | 'how'
    const [localPins, setLocalPins] = useState(readLocalPins);
    const [pinOverride, setPinOverride] = useState({});
    const [scrolled, setScrolled] = useState(false);
    const [petSeenAt, setPetSeenAt] = useState(0);
    // Conversations sit beside the thread when there's room, as in ChatGPT
    // and Claude. The choice to hide them is remembered in this browser.
    const [vw, setVw] = useState(() => window.innerWidth);
    const wide = vw >= 1100;
    // Docked: open or hidden, remembered in this browser once they choose.
    const [sidePref, setSidePref] = useState(() => {
        try { return localStorage.getItem('bly-side'); } catch (e) { return null; }
    });
    // Over the page: open only until they pick something or close it.
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [peekAllowed, setPeekAllowed] = useState(false);
    const peek = usePeek(peekAllowed);
    const [navFolded, setNavFolded] = useState(() => document.body.classList.contains('nav_menu_open'));
    const toggleRef = useRef(null);
    // Whether they already run loyalty. A proposal must never silently
    // replace a card clients are part-way through. Seeded from the cache,
    // then reconciled with the server below.
    const [loyaltyScheme, setLoyaltyScheme] = useState(() => {
        try {
            const user = JSON.parse(localStorage.getItem('user')) || {};
            return loadScheme(user._id) || null;
        } catch (e) { return null; }
    });

    const [look, setLook] = useState(() => normaliseAppearance(null));
    const [savedLook, setSavedLook] = useState(() => normaliseAppearance(null));
    const [styling, setStyling] = useState(false);
    const [savingLook, setSavingLook] = useState(false);
    const [lookError, setLookError] = useState(null);
    // Whether the API stores the style chair's newer fields yet (lookStore.js).
    const [lookExtras, setLookExtras] = useState({ stored: false, offered: extrasAvailable(null) });
    const [motionPaused, setMotionPaused] = useState(false);
    const [completed, setCompleted] = useState(false);
    const [activeTool, setActiveTool] = useState(null);
    const [attentive, setAttentive] = useState(false);
    const mounted = useRef(true);
    const abortStream = useRef(null);
    const sendingRef = useRef(false);
    const scrollRef = useRef(null);
    const inputRef = useRef(null);
    const shellRef = useRef(null);
    const [shell, setShell] = useState(null);     // { top, height }
    const pill = useSetupPill();
    // Bailey's second job. Loaded once the tab knows this person may see it.
    const accountRole = useSelector((st) => getUserRole(st.user));
    const fdAllowed = frontDeskAllowed(status, accountRole);
    // Loaded whatever the owner-side Bailey says: the front desk is the salon's.
    const fd = useFrontDesk({ enabled: fdAllowed });
    const page = pageFor(props.location && props.location.pathname);
    const go = useCallback((path) => { hapticTick(); if (props.history) props.history.push(path); }, [props.history]);
    // Whether the place now showing is one being come back to: the browser's
    // back, the tab's own back, or home from a conversation. Worked out as
    // it renders, so the place's scroll area knows when it mounts.
    const returning = useRef(false);
    const lastKey = useRef(props.location && props.location.key);
    if (props.location && props.location.key !== lastKey.current) {
        lastKey.current = props.location.key;
        if (props.history && props.history.action === 'POP') returning.current = true;
    }
    const place = page || (view === 'thread' ? 'thread' : 'home');
    useEffect(() => { returning.current = false; }, [place]);
    const sessionIdRef = useRef(null);
    sessionIdRef.current = sessionId;

    useEffect(() => () => {
        mounted.current = false;
        if (abortStream.current) abortStream.current();
    }, []);

    // The page is sized to the window. Global CSS from the booking screen
    // pads <body> by 70px on phones, which let the whole page slide under
    // the fixed header; this class cancels it while Bailey is open.
    useEffect(() => {
        document.body.classList.add('bly-on');
        return () => document.body.classList.remove('bly-on');
    }, []);

    // Ask the server what the truth is, and let hydrateLoyalty correct the
    // cache. Failure keeps the cache, so a flaky call never invents or hides
    // a scheme.
    useEffect(() => {
        let user = {};
        try { user = JSON.parse(localStorage.getItem('user')) || {}; } catch (e) { /* no id */ }
        hydrateLoyalty(user._id)
            .then((r) => { if (mounted.current) setLoyaltyScheme((r && r.scheme) || null); })
            .catch(() => { /* hydrate already falls back to the cache */ });
    }, []);

    /**
     * Go to the screen that actually edits a thing. The backend says where
     * each draft opens, so a new kind of draft needs no change here.
     */
    const goTo = (to) => {
        if (!to || !to.path) return;
        if (props && props.history) props.history.push(to.path, to.state || {});
        else window.location.assign(to.path);
    };

    /**
     * Open a draft IN the conversation, so the thread (what Bailey suggested
     * and why) stays on screen; the full editor is a deliberate second step.
     */
    const loadDraft = async (kind, id) => {
        const key = `${kind}:${id}`;
        const patch = (fields) => setMessages(m => m.map(msg => msg.cards
            ? { ...msg, cards: msg.cards.map(c =>
                c.type === 'draft' && c.key === key ? { ...c, ...fields } : c) }
            : msg));

        patch({ loading: true, error: undefined });
        try {
            const res = await agentService.draft(kind, id);
            const payload = res && res.data && res.data.responseData;
            if (!mounted.current) return;
            // A 200 with nothing in it is still a failure to open.
            patch(payload ? { loading: false, draft: payload } : { loading: false, error: '' });
        } catch (e) {
            if (!mounted.current) return;
            patch({ loading: false, error: '' });
        }
    };

    const openDraft = (d) => {
        if (!d) return;
        const key = `${d.kind}:${d.id}`;
        const already = messages.some(m =>
            (m.cards || []).some(c => c.type === 'draft' && c.key === key));
        if (!already) {
            if (!messages.length) setTitle(d.title || 'Draft');
            setMessages(m => [...m, {
                role: 'them', text: '', local: true,
                cards: [{ type: 'draft', key, kind: d.kind, draftId: d.id, loading: true }]
            }]);
        }
        setView('thread');
        // Retry comes through here too, so it re-fetches.
        loadDraft(d.kind, d.id);
    };

    useEffect(() => {
        if (!completed) return undefined;
        const timer = setTimeout(() => setCompleted(false), 2000);
        return () => clearTimeout(timer);
    }, [completed]);

    const closeStyling = () => {
        setLook(savedLook);
        setLookError(null);
        setStyling(false);
    };

    // Resolves true once saved. The style chair then plays its reveal and
    // closes itself (onDone), so this never closes it.
    const saveLook = async () => {
        if (savingLook) return false;
        setSavingLook(true);
        setLookError(null);
        try {
            const res = await agentService.setAppearance(savePayload(look, lookExtras.stored));
            if (!mounted.current) return false;
            const data = (res.data && res.data.responseData) || {};
            const saved = afterSave(data.appearance || data, look);
            if (serverHasExtras(data.appearance || data)) setLookExtras({ stored: true, offered: true });
            setSavedLook(saved);
            rememberLook(saved);
            setLook(saved);
            return true;
        } catch (e) {
            if (mounted.current) setLookError('Couldn’t save your look. Please try again.');
            return false;
        } finally {
            if (mounted.current) setSavingLook(false);
        }
    };

    // Bumped after switching Bailey on from the opt-in screen: reloads status.
    const [statusKey, setStatusKey] = useState(0);
    useEffect(() => {
        let alive = true;
        agentService.status()
            .then(res => {
                if (!alive) return;
                const d = (res.data && res.data.responseData) || {};
                const selected = resolveAppearance(d) || normaliseAppearance(null);
                rememberLook(selected);
                // One render: the look lands with the page, so Bailey never
                // appears in the default look first (a promise callback isn't
                // batched by React 16 on its own).
                batch(() => {
                    setLook(selected);
                    setSavedLook(selected);
                    setLookExtras({ stored: serverHasExtras(d.appearance), offered: extrasAvailable(d.appearance) });
                    setStatus(d);
                    setReady(!!d.available);
                });
            })
            .catch(() => { if (alive) setReady(false); });
        return () => { alive = false; };
    }, [statusKey]);

    /**
     * What is waiting, and what was being worked on. Approvals belong on the
     * home screen: inside a thread, a reload hid the one item with a cost.
     */
    const loadLanding = useCallback(() => {
        agentService.asks()
            .then(res => setWaiting((res.data && res.data.responseData) || []))
            .catch(() => setWaiting([]));
        convos.refresh();
        agentService.goals()
            .then(res => setGoals((res.data && res.data.responseData) || []))
            .catch(() => setGoals([]));
        // Without this, "I've drafted that for you" is a dead end.
        agentService.drafts()
            .then(res => setDrafts((res.data && res.data.responseData) || []))
            .catch(() => setDrafts([]));
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { if (ready === true) loadLanding(); }, [ready, loadLanding]);

    // The front desk's pages: a salon that has never used it starts with the
    // switch-on, and someone who may not see it goes back to the home.
    // Decided once per arrival: switching the last thing off on the page
    // mustn't throw them into the switch-on mid-visit.
    const arrival = useRef('');
    useEffect(() => {
        if (!page || !status || !props.history) return;
        if (!fdAllowed) { if (ready === true) props.history.replace('/assistant'); return; }
        if (page !== 'front-desk' || !fd.loaded) return;
        const here = (props.location && props.location.key) || 'first';
        if (arrival.current === here) return;
        arrival.current = here;
        if (fd.never) props.history.replace({ pathname: '/assistant/front-desk/setup', state: props.location && props.location.state });
    }, [page, ready, status, fdAllowed, fd.loaded, fd.never]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const onResize = () => setVw(window.innerWidth);
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, []);
    const setSide = (v) => {
        setSidePref(v);
        try { localStorage.setItem('bly-side', v); } catch (e) { /* per visit then */ }
    };

    // "I noticed", once the things it reads from have loaded.
    const toolsKey = ((status && status.tools) || []).join(',');
    const draftsKey = drafts.map(d => `${d.kind}${d.id}${d.detail}`).join('|');
    useEffect(() => {
        if (ready !== true) return undefined;
        let alive = true;
        loadIdeas({ drafts, loyaltyScheme, tools: (status && status.tools) || [] })
            .then((list) => { if (alive) setNoticed(list); })
            .catch(() => { if (alive) setNoticed([]); });
        return () => { alive = false; };
    }, [ready, toolsKey, draftsKey, !!loyaltyScheme]); // eslint-disable-line react-hooks/exhaustive-deps

    const onThreadScroll = (e) => {
        const on = e.currentTarget.scrollTop > 4;
        if (on !== scrolled) setScrolled(on);
    };

    // Keep the newest message in view.
    useEffect(() => {
        const el = scrollRef.current;
        if (el && view === 'thread') el.scrollTop = el.scrollHeight;
    }, [messages, sending, view]);

    /**
     * Size the page to whatever room the app shell leaves, MEASURED rather
     * than a magic number: the side nav collapses, the top offset differs per
     * breakpoint, and phones have a tab bar.
     */
    useEffect(() => {
        const measure = () => {
            setNavFolded(document.body.classList.contains('nav_menu_open'));
            // Bailey draws its own header, so the page is the whole window.
            const top = 0;
            setShell({ top, height: Math.max(360, Math.round(window.innerHeight - top)) });
        };
        measure();
        window.addEventListener('resize', measure);
        const mo = new MutationObserver(measure);
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => { window.removeEventListener('resize', measure); mo.disconnect(); };
    }, [ready]);

    const patchLast = (fn) => setMessages(m => {
        const copy = m.slice();
        for (let i = copy.length - 1; i >= 0; i--) {
            if (copy[i].role !== 'me') { copy[i] = fn(copy[i]); break; }
        }
        return copy;
    });

    const send = useCallback((textIn) => {
        // "B" answers the lettered question on screen with its words
        const typed = String(textIn == null ? draft : textIn);
        const text = letteredAnswer(typed) || typed.trim();
        if (!text || sendingRef.current) return;

        setError(null);
        setCompleted(false);
        setActiveTool(null);
        setDraft('');
        setSending(true);
        setView('thread');
        sendingRef.current = true;
        setTitle(t => t || threadTitle({ title: text }));
        // The reply goes in EMPTY and fills as text arrives, with the steps
        // Bailey takes listed as it takes them.
        setMessages(m => [...m,
            { role: 'me', text },
            { role: 'them', text: '', streaming: true, steps: [] }
        ]);

        // The desk pet shows what Bailey is doing in every other tab.
        let streamed = '';
        publishRun({ phase: 'working', title: text, activity: 'Thinking', tool: null, preview: '', sessionId, source: 'page' });
        // Their words go with the app's context (where they were, what's on
        // screen, the brand, and all of Beu for a new conversation); it's
        // taken off again wherever the message is shown
        // "Yes" to what Bailey just showed saves it first (saveBus), and the
        // turn says what was saved
        // "Less formal" / "make them pink" changes them in place (saveBus).
        // What's on screen (plans) goes too, so Bailey can change a plan.
        const lastReply = ([...messagesRef.current].reverse().find(m => m.role === 'them') || {}).text || '';
        const saving = yesSaves(text, lastReply) && hasSavers() ? saveLatest().then(savedLine).catch(() => '')
            : isChangeIntent(text) && hasEditors() ? (publishRun({ activity: 'Changing what Bailey showed' }), editLatest(text).then(l => changedLine(l, text)).catch(() => ''))
                : Promise.resolve('');
        saving.then((done) => {
        if (!mounted.current) return;
        const out = messageFor(text, { surface: 'tab', sessionId, saved: [done, ...screenFacts()].filter(Boolean).join('\n') });
        abortStream.current = agentService.chatStream({ sessionId, message: out.message, appContext: out.appContext, goalId: threadGoalId }, (ev) => {
            if (!mounted.current) return;
            switch (ev.type) {
                case 'text_delta':
                    streamed += ev.text || '';
                    setActiveTool(null);
                    patchLast(mm => ({ ...mm, text: mm.text + ev.text, steps: doneSteps(mm.steps) }));
                    break;
                case 'thinking':
                    setActiveTool(null);
                    publishRun({ activity: 'Thinking', tool: null });
                    break;
                case 'tool_start':
                    setActiveTool(ev.tool || null);
                    publishRun({ activity: ev.label || 'Working on it', tool: ev.tool || null });
                    patchLast(mm => ({
                        ...mm,
                        steps: doneSteps(mm.steps).concat({ tool: ev.tool || null, label: ev.label || 'Working on it', done: false })
                    }));
                    break;
                case 'tool_done':
                    patchLast(mm => ({ ...mm, steps: doneSteps(mm.steps) }));
                    break;
                case 'done':
                    setCompleted(true);
                    bumpBaileyUsed();
                    publishRun({ phase: 'done', activity: null, tool: null, sessionId: ev.sessionId || sessionId, preview: readyLine(ev.content || streamed) || previewOf(tidyWords(ev.content || streamed)) || 'Done.' });
                    if (ev.sessionId) {
                        setSessionId(ev.sessionId);
                        remember(ev.sessionId);
                    }
                    if (out.full) markBriefed(ev.sessionId || sessionId);
                    patchLast(mm => ({
                        ...mm,
                        // Prefer the final text: a dropped frame mid-stream
                        // would otherwise leave a truncated answer.
                        text: ev.content || mm.text,
                        streaming: false,
                        steps: doneSteps(mm.steps),
                        // Cards come from the SERVER's tool results, never
                        // parsed out of prose.
                        cards: ev.cards && ev.cards.length ? ev.cards : null,
                        followUps: Array.isArray(ev.followUps) ? ev.followUps : undefined
                    }));
                    break;
                case 'error': {
                    // Out of free questions: a friendly card in the thread with
                    // the upgrade, never an error banner.
                    const quota = quotaRefusal(ev);
                    if (quota) {
                        markBaileyOut(quota);
                        publishRun({ phase: 'done', activity: null, tool: null, preview: quota.code === 'bailey_staff_access' ? 'Bailey isn’t switched on for you.' : 'This month’s questions are used.' });
                        patchLast(mm => ({ ...mm, streaming: false, steps: doneSteps(mm.steps), cards: [{ type: 'bailey_quota', quota }] }));
                        break;
                    }
                    setCompleted(false);
                    publishRun({ phase: 'error', activity: null, tool: null, preview: ev.message || 'Bailey couldn’t answer just now.' });
                    setError(ev.message || 'Something went wrong. Try again.');
                    hapticError();
                    patchLast(mm => ({ ...mm, streaming: false }));
                    break;
                }
                case 'end':
                    sendingRef.current = false;
                    abortStream.current = null;
                    setSending(false);
                    patchLast(mm => ({ ...mm, streaming: false }));
                    loadLanding();
                    if (inputRef.current && window.innerWidth > 640) inputRef.current.focus();
                    break;
                default:
                    break;
            }
        });
        });
        // `sending` is deliberately not a dependency: the guard is sendingRef.
    }, [draft, sessionId, loadLanding, threadGoalId]); // eslint-disable-line react-hooks/exhaustive-deps

    // The approval card in the thread, marked with what came of it
    const markApproval = useCallback((id, fields) => setMessages(m => m.map(msg => msg.cards
        ? { ...msg, cards: msg.cards.map(c => (c.type === 'approval' && c.approvalId === id ? { ...c, ...fields } : c)) }
        : msg)), []);

    const onApprove = useCallback(async (id) => {
        setBusyApproval(id);
        setError(null);
        let outcome;
        try {
            // Only ever `true` from here: an approval is a yes, nothing else
            outcome = readApproval(await agentService.answerAsk(id, true));
        } catch (err) {
            hapticError();
            setError(approvalErrorOf(err));
            setBusyApproval(null);
            loadLanding();
            return;
        }
        try {
            if (outcome.ok) {
                hapticSuccess();
                markApproval(id, { approved: true });
                // The server ran the PINNED action. Ask the agent to report back
                // rather than inventing a confirmation here: this screen must
                // never claim something happened.
                await send('I’ve approved it. What happened?');
            } else {
                // Approved, but the action itself was refused (no credits,
                // nothing to send…). Say so, and tell Bailey it didn't go
                // rather than asking it to report a success.
                hapticError();
                markApproval(id, { failed: outcome.reason });
                setError(`That didn’t go: ${outcome.reason}`);
                await send(`I approved it but it didn’t go: ${outcome.reason}`);
            }
        } finally {
            setBusyApproval(null);
            loadLanding();
        }
    }, [send, loadLanding, markApproval]);

    const onReject = useCallback(async (id) => {
        setBusyApproval(id);
        try { await agentService.dismissAsk(id); } catch (e) { /* it is gone either way */ }
        setMessages(m => m.map(msg => msg.cards
            ? { ...msg, cards: msg.cards.filter(c => c.approvalId !== id) }
            : msg));
        setBusyApproval(null);
        loadLanding();
    }, [loadLanding]);

    const clearThread = () => {
        setMessages([]);
        setSessionId(null);
        setTitle('');
        setError(null);
        setThreadGoalId(null);
        try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* ignore */ }
    };

    /**
     * Back to the home screen. The conversation is kept on the server and sits
     * at the top of Recent conversations. While Bailey is still answering, the
     * thread stays in memory so the answer isn't lost, and home offers a way
     * back into it.
     */
    // Leaving for another page counts from when they left, not when it opened.
    useEffect(() => () => { if (sessionIdRef.current) remember(sessionIdRef.current); }, []);

    // Home from a conversation is a way back, so the home is where it was
    // left; the Needs you pill is a way to what's waiting, at the top.
    const goHome = (opts) => {
        hapticTick();
        returning.current = !(opts && opts.top);
        setView('home');
        if (!sendingRef.current) clearThread();
        loadLanding();
    };

    const newConversation = () => {
        if (sendingRef.current) return;
        clearThread();
        setView('home');
        requestAnimationFrame(() => { if (inputRef.current) inputRef.current.focus(); });
    };

    /** Load a thread into the view. */
    const resume = useCallback(async (id) => {
        if (sendingRef.current) return;
        setError(null);
        setCompleted(false);
        try {
            const res = await agentService.transcript(id);
            const d = (res.data && res.data.responseData) || {};
            const rows = d.rows || [];
            // Tool rows are internal plumbing; a person wants the conversation.
            // The drafts Bailey made come back as cards, opened live.
            const restored = messagesFromTranscript(rows, stripContext);
            setMessages(restored);
            draftsToOpen(restored).forEach(d => loadDraft(d.kind, d.id));
            setSessionId(id);
            setThreadGoalId((d.session && d.session.goalId) || null);
            const row = recent.find(r => String(r._id) === String(id)) || d.session || {};
            const firstMine = rows.find(r => r.role === 'user');
            setTitle(threadTitle(row.title || row.summaryTitle ? row : { title: firstMine && stripContext(firstMine.content) }));
            setView('thread');
            remember(id);
        } catch (err) {
            setError('I couldn’t reopen that conversation. Try again.');
        }
    }, [recent]);

    /**
     * Open the conversation about one goal. Reuses its thread if there is one,
     * so tapping a goal twice doesn't leave two half-conversations.
     */
    const openGoal = useCallback(async (goal) => {
        if (sendingRef.current) return;
        setError(null);
        try {
            const res = await agentService.goalThread(goal._id);
            const existing = (res.data && res.data.responseData && res.data.responseData.sessionId) || null;
            if (existing) { await resume(existing); setThreadGoalId(goal._id); setTitle(goal.title); return; }
        } catch (e) { /* fall through to a fresh thread */ }
        clearThread();
        setThreadGoalId(goal._id);
        setTitle(goal.title);
        setView('thread');
        requestAnimationFrame(() => { if (inputRef.current) inputRef.current.focus(); });
    }, [resume]); // eslint-disable-line react-hooks/exhaustive-deps

    // Opened from the desk pet (/assistant?session=<id>), or coming back to a
    // thread left open before visiting another page. Or sent something to
    // do from search (state.ask): start a new conversation with it, once.
    useEffect(() => {
        if (ready !== true) return;
        const loc = props.location;
        const asked = loc && loc.state && typeof loc.state.ask === 'string' ? loc.state.ask.trim() : '';
        if (asked) {
            if (props.history) props.history.replace({ pathname: loc.pathname, search: loc.search, state: undefined });
            send(asked.slice(0, 2000));
            return;
        }
        let id = null;
        try { id = new URLSearchParams(window.location.search).get('session'); } catch (e) { /* ignore */ }
        if (id) {
            const params = new URLSearchParams(window.location.search);
            params.delete('session');
            const q = params.toString();
            window.history.replaceState(window.history.state, '', `${window.location.pathname}${q ? `?${q}` : ''}`);
        } else {
            id = recall();
        }
        if (id) resume(id);
    }, [ready]); // eslint-disable-line react-hooks/exhaustive-deps

    // A reply the pet started may still be running when this thread opens.
    // Show Bailey at it, and reload the transcript once it lands.
    const petRun = useBaileyRun();
    const petRunning = !!(petRun && petRun.phase === 'working' && petRun.sessionId
        && petRun.sessionId === sessionId && !sendingRef.current);
    const petRunAt = petRun && petRun.at;
    const petRunPhase = petRun && petRun.phase;
    const petRunSession = petRun && petRun.sessionId;
    const wasPetRunning = useRef(false);
    useEffect(() => {
        if (wasPetRunning.current && petRunPhase !== 'working' && petRunSession === sessionId) resume(sessionId);
        wasPetRunning.current = petRunning;
    }, [petRunning, petRunAt, petRunPhase, petRunSession, sessionId, resume]);

    // Home's cards, worded from this salon's own facts once they're in.
    const [glance, setGlance] = useState(null);
    useEffect(() => {
        if (ready !== true) return undefined;
        let alive = true;
        agentService.glance()
            .then((res) => { if (alive) setGlance((res.data && res.data.responseData) || null); })
            .catch(() => {});
        return () => { alive = false; };
    }, [ready]);
    const ideas = useMemo(() => ideasFor(status, glance), [status, glance]);

    /* ----- pinned conversations ----- */
    const fromServer = serverPins(recent);
    const isPinned = useCallback((id) => {
        const k = String(id);
        if (Object.prototype.hasOwnProperty.call(pinOverride, k)) return pinOverride[k];
        if (fromServer) { const r = recent.find(x => String(x._id) === k); return !!(r && r.pinned); }
        return localPins.some(p => String(p._id) === k);
    }, [pinOverride, fromServer, recent, localPins]);
    const pinnedRows = useMemo(() => {
        const base = fromServer ? recent : localPins.map(p => recent.find(r => String(r._id) === String(p._id)) || p);
        const out = base.filter(r => isPinned(r._id));
        // Pinned a moment ago but not in the list yet.
        Object.keys(pinOverride).forEach((k) => {
            if (pinOverride[k] && !out.some(r => String(r._id) === k)) {
                const r = recent.find(x => String(x._id) === k);
                if (r) out.unshift(r);
            }
        });
        return out;
    }, [fromServer, recent, localPins, isPinned, pinOverride]);
    const togglePin = useCallback(async (row) => {
        const k = String(row._id);
        const next = !isPinned(k);
        setPinOverride(o => ({ ...o, [k]: next }));
        try {
            const list = await setPinned(row, next, recent);
            if (list) {
                setLocalPins(list);
                setPinOverride(o => { const c = { ...o }; delete c[k]; return c; });
            } else {
                convos.refresh();
            }
        } catch (e) {
            setPinOverride(o => { const c = { ...o }; delete c[k]; return c; });
            hapticError();
            setError('I couldn’t pin that just now. Try again.');
        }
    }, [isPinned, recent]); // eslint-disable-line react-hooks/exhaustive-deps

    /**
     * The x on a conversation. Soft delete: the server keeps the thread for
     * training, the person never sees it again. It leaves the list (and the
     * pinned block — the server unpins it) straight away; if it was the open
     * thread, back home. Put back if the server says no.
     */
    // "Conversation deleted · Undo", for a few seconds after a delete.
    const [undo, setUndo] = useState(null);
    const undoTimer = useRef(null);
    useEffect(() => () => clearTimeout(undoTimer.current), []);
    const offerUndo = useCallback((row) => {
        clearTimeout(undoTimer.current);
        setUndo({
            run: async () => {
                clearTimeout(undoTimer.current);
                setUndo(null);
                try {
                    await agentService.restoreSession(row._id);
                } catch (e) {
                    hapticError();
                    setError('I couldn’t bring that conversation back.');
                }
                convos.refresh();
            }
        });
        undoTimer.current = setTimeout(() => setUndo(null), 8000);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const deleteThread = useCallback(async (row) => {
        const k = String(row._id);
        const wasOpen = String(sessionId) === k;
        convos.remove(k);
        setPinOverride(o => ({ ...o, [k]: false }));
        setLocalPins(list => list.filter(p => String(p._id) !== k));
        if (wasOpen) goHome();
        try {
            await agentService.deleteSession(k);
            setPinOverride(o => { const c = { ...o }; delete c[k]; return c; });
            offerUndo(row);
        } catch (e) {
            setPinOverride(o => { const c = { ...o }; delete c[k]; return c; });
            hapticError();
            setError('I couldn’t delete that conversation just now. Try again.');
        }
        convos.refresh();
    }, [sessionId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Bailey is on for this account but this person hasn't switched it on
    // yet: say what it is and let them, rather than a blank page. (Nothing in
    // the app called agentService.setEnabled before, so a new owner, stylist
    // or member of staff could never get past this.)
    // A client on the line shows here too: Bailey picks up the handset.
    const onCall = isCall(petRun) && !sending && !petRunning;
    const salonName = (status && status.salonName) || '';
    // Back from one of the tab's own pages: the switch-on and the soul return
    // to the front desk, the front desk to the home.
    const backFromPage = () => {
        if (!props.history) return;
        // Pushed here from inside Beu (or redirected from Settings, which
        // says so): back is a real back, to exactly where they were. Opened
        // fresh or by the browser's own back: up to the page above instead.
        const cameIn = props.history.action === 'PUSH' || !!(props.location && props.location.state && props.location.state.inApp);
        if (cameIn) { returning.current = true; props.history.goBack(); return; }
        props.history.replace(page === 'front-desk' || (page === 'setup' && fd.never) ? '/assistant' : '/assistant/front-desk');
    };
    const pageBody = page && (
        <PlaceScroll key={page} place={page} restore={returning.current}>
            {page === 'front-desk' && (
                <FrontDeskPage fd={fd} onCall={onCall} history={props.history} salonName={salonName}
                    onOpenSoul={() => go('/assistant/soul')}
                    onOpenConversations={() => go('/assistant/front-desk/conversations')}
                    onOpenMessages={() => go('/assistant/front-desk/conversations?show=messages')} />
            )}
            {page === 'conversations' && (
                <ConversationsPage fd={fd} history={props.history} salonName={salonName} look={look}
                    startOn={/(^|[?&])show=messages(&|$)/.test((props.location && props.location.search) || '') ? 'msg' : 'all'} />
            )}
            {page === 'setup' && <FrontDeskSetup fd={fd} look={look} salonName={salonName} motionPaused={motionPaused} onDone={() => props.history && props.history.replace('/assistant/front-desk')} onNotNow={backFromPage} />}
            {page === 'soul' && <SoulPage fd={fd} look={look} salonName={salonName} motionPaused={motionPaused} onSaved={backFromPage} onCancel={backFromPage} />}
        </PlaceScroll>
    );
    // The owner-side Bailey is off or unavailable, but the front desk still
    // opens: it doesn't need it. Back goes to the home, where the switch-on is.
    if (ready === false && page && fdAllowed) {
        return (
            <FrontDeskShell title={PAGE_TITLES[page]} style={shell ? { marginTop: shell.top, height: shell.height } : undefined}
                onBack={backFromPage}>
                {pageBody}
            </FrontDeskShell>
        );
    }
    // One branch for both (a second early return trips the hooks lint's
    // path counter): an employed team member the owner hasn't switched on
    // is told who to ask; anyone else not switched on gets the switch.
    if (ready === false && status && (status.reason === 'not-enabled' || status.reason === 'staff-not-enabled')) {
        if (status.reason === 'staff-not-enabled') return <BaileyLocked status={status} frontDesk={fdAllowed} onFrontDesk={() => go('/assistant/front-desk')} />;
        return <BaileyOptIn status={status} frontDesk={fdAllowed} onFrontDesk={() => go('/assistant/front-desk')} onEnabled={() => { setReady(null); setStatusKey((k) => k + 1); }} />;
    }
    // Still asking, or the feature is off: render NOTHING. An absent feature
    // should look absent.
    if (ready !== true) return null;
    const baileyState = getBaileyState({ sending: sending || petRunning || onCall, busyApproval, error, waiting: waiting.length > 0, completed, attentive });
    const avatarActivity = getBaileyActivity(baileyState, busyApproval ? null : (onCall ? CALL_TOOL : activeTool));
    // Derived from what the server actually offers, not a build flag.
    const canAct = !!(status && (status.tools || []).some(
        (n) => n === 'send_campaign' || n === 'draft_campaign'));

    const side = drawerLayout({ vw, navFolded, sidePref, drawerOpen });
    // The front desk's pages are places you visit, like the style chair: no conversations beside them.
    const showSide = side.dock && side.visible && !page;
    // Peeking only makes sense where it would otherwise dock, and is hidden.
    const canPeek = side.dock && !side.visible && !styling && !page;
    if (canPeek !== peekAllowed) setPeekAllowed(canPeek);
    const pinOK = pinsAllowed(recent);
    const openConversations = () => { hapticTick(); if (side.dock) setSide('open'); else setDrawerOpen(true); };
    const currentRow = sessionId ? (recent.find(r => String(r._id) === String(sessionId)) || { _id: sessionId, title }) : null;
    const listProps = {
        convos,
        pinned: pinnedRows,
        isPinned,
        canPin: pinOK,
        activeId: view === 'thread' ? sessionId : null,
        onOpen: resume,
        onTogglePin: togglePin,
        onDelete: deleteThread,
        undo,
        atHome: view === 'home' && !styling && !page,
        // Another thread can't open while this one's reply is still arriving.
        locked: sending
    };
    // Home shows the three latest, pinned ones first.
    const homeRecent = pinnedRows.concat(recent.filter(r => !isPinned(r._id)));

    const menuItems = [
        view === 'thread' && currentRow && pinOK && {
            label: isPinned(currentRow._id) ? 'Unpin conversation' : 'Pin conversation',
            icon: <Icon.Pin size={18} filled={isPinned(currentRow._id)} />,
            onClick: () => togglePin(currentRow)
        },
        view === 'thread' && currentRow && !sending && {
            label: 'Delete conversation',
            icon: <Icon.Close size={18} />,
            onClick: () => deleteThread(currentRow)
        },
        { label: 'How I work', icon: <Icon.Shield size={18} />, onClick: () => setPanel('how') },
        fdAllowed && page !== 'front-desk' && { label: 'Front desk', icon: <Icon.Headset size={18} />, onClick: () => go('/assistant/front-desk') },
        fdAllowed && page !== 'soul' && { label: 'Bailey’s soul', icon: <Icon.Soul size={18} />, onClick: () => go('/assistant/soul') },
        { label: 'Style Bailey', icon: <Icon.Brush size={18} />, onClick: () => setStyling(true) },
        {
            label: motionPaused ? 'Turn motion on' : 'Pause motion',
            icon: motionPaused ? <Icon.Motion size={18} /> : <Icon.Pause size={18} />,
            onClick: () => setMotionPaused(v => !v)
        }
    ];

    const onIdea = (idea) => {
        if (idea.go) { go(idea.go); return; }
        if (idea.seed) {
            setDraft(idea.seed);
            requestAnimationFrame(() => {
                const el = inputRef.current;
                if (!el) return;
                el.focus();
                try { el.setSelectionRange(idea.seed.length, idea.seed.length); } catch (e) { /* ignore */ }
                el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            });
            return;
        }
        send(idea.prompt || idea.title);
    };
    const onNoticed = (n) => {
        if (n.draft) { openDraft(n.draft); return; }
        send(n.prompt || n.title);
    };

    const shown = petRunning
        ? [...messages, { role: 'them', text: '', streaming: true, steps: petRun.activity ? [{ tool: petRun.tool || null, label: petRun.activity, done: false }] : [] }]
        : messages;
    const lastThem = (() => { for (let i = shown.length - 1; i >= 0; i--) if (shown[i].role !== 'me') return i; return -1; })();
    // A reply that asks its own question (a choices block) has its answers already
    const asks = lastThem !== -1 && asksToTap(splitReply(shown[lastThem].text));
    const lastFollow = lastThem === shown.length - 1 && !sending && !asks ? followUpsFor(shown[lastThem]) : [];

    // A draft revised in a later turn shows once, on the latest turn.
    const draftHomes = draftCardHomes(shown);
    const renderCard = (i) => (card, ci) => (!showsDraftCardAt(draftHomes, card, i) ? null : (
        <div className="bly-card-slot" key={ci}>
            {card.type === 'metrics' && <MetricsCard card={card} />}
            {card.type === 'bailey_quota' && <BaileyQuotaCard quota={card.quota} isOwner={isOwnerHere()} />}
            {card.type === 'builder' && (
                <BuilderCard
                    card={card}
                    salonName={(status && status.salonName) || ''}
                    // One scheme per salon and saving replaces it, so the card
                    // has to know whether clients are already collecting.
                    existingScheme={loyaltyScheme}
                    onSave={async (scheme) => {
                        let ownerId = null;
                        try { ownerId = (JSON.parse(localStorage.getItem('user')) || {})._id; }
                        catch (e) { /* the store handles a missing id */ }
                        // Awaited, so a refused save reaches the card as an error.
                        const saved = await saveSchemeAsync(ownerId, scheme);
                        setLoyaltyScheme(saved);
                        loadLanding();
                    }}
                />
            )}
            {/* A campaign Bailey drafted: drawn as the client gets it (words-only
                drafts in the salon's template), one tap from finishing */}
            {card.type === 'draft' && card.kind === 'campaign' && (
                <DraftWidget
                    draft={card.draft}
                    loading={card.loading}
                    error={card.error}
                    revised={!!card.revised}
                    go={(path, state) => props.history && props.history.push(path, state || {})}
                    onRetry={() => openDraft({ kind: card.kind, id: card.draftId })}
                />
            )}
            {card.type === 'draft' && card.kind !== 'campaign' && (
                <DraftCard
                    draft={card.draft}
                    loading={card.loading}
                    error={card.error}
                    revised={!!card.revised}
                    onRetry={() => openDraft({ kind: card.kind, id: card.draftId })}
                    onOpenFull={() => goTo(card.draft && card.draft.opensAt)}
                />
            )}
            {card.type === 'diary' && <DiaryCard card={card} />}
            {['booking', 'event', 'leave'].includes(card.type) && (
                <BookingCard card={card} onOpen={() => openProposal(card, props.history)} />
            )}
            {card.type === 'setup' && <SetupCard items={card.items || []} />}
            {card.type === 'approval' && (
                <ApprovalCard
                    approval={card}
                    busy={busyApproval === card.approvalId}
                    onApprove={onApprove}
                    onReject={onReject}
                />
            )}
        </div>
    ));

    const inThread = view === 'thread' && !styling && !page;
    // Bailey's questions, and messages he took until the merge moves those to
    // Messages. Client chats he hands over count on the Messages badge, where
    // they're answered (one home per job, Ben 13 Oct 2026; brief section 27)
    const needsCount = waiting.length + (fdAllowed ? fd.open.length : 0);
    // One header for the whole tab, in place of the app's TopHeader and a
    // second bar under it: the way round on the left, what this is in the
    // middle, and what needs you on the right.
    const header = (
        <header className={`bly-head${inThread && scrolled ? ' is-scrolled' : ''}`}>
            {styling || page ? (
                // The style chair and the front desk's pages are places you visit,
                // so the way out is back, at every width. Same as the chair's
                // Cancel: unsaved changes go.
                <button type="button" className="bly-app-menu is-back" disabled={savingLook} onClick={() => { hapticTick(); if (styling) closeStyling(); else backFromPage(); }} aria-label="Back">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="15 18 9 12 15 6" />
                    </svg>
                </button>
            ) : !wide && (
                // The app's own menu button, drawn and placed as on every other
                // screen below 1100px, unread dot included.
                <button type="button" className="bly-app-menu" onClick={() => { hapticTick(); document.body.classList.toggle('nav_menu_open'); }} aria-label="Menu">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                </button>
            )}
            {!side.visible && !styling && !page && (
                <button
                    type="button"
                    ref={toggleRef}
                    className="bly-iconbtn"
                    onClick={() => { peek.close(); openConversations(); }}
                    onMouseEnter={peek.enter}
                    onMouseLeave={peek.leave}
                    aria-label="Show conversations"
                    title={canPeek ? undefined : 'Conversations'}
                >
                    <Icon.Sidebar size={20} />
                </button>
            )}
            {inThread && !side.visible && (
                <button type="button" className="bly-iconbtn" onClick={() => (sending ? goHome() : newConversation())} aria-label="New conversation" title="New conversation">
                    <Icon.Compose size={19} />
                </button>
            )}
            <div className="bly-head-who">
                {page && (
                    <div className="bly-head-copy"><b>{PAGE_TITLES[page]}</b></div>
                )}
                {inThread && (
                    <>
                        <div className="bly-head-avatar">
                            <BaileyAvatar appearance={look} state={baileyState} activity={avatarActivity} paused={motionPaused} />
                        </div>
                        <div className="bly-head-copy">
                            <b>
                                {currentRow && isPinned(currentRow._id) && <span className="bly-bar-pin" aria-label="Pinned"><Icon.Pin size={13} filled /></span>}
                                {title || 'New conversation'}
                            </b>
                            <span className={`bly-status is-${baileyState}`}><i />{STATE_LABELS[baileyState]}</span>
                        </div>
                    </>
                )}
            </div>
            <div className="bly-head-right">
                {inThread && !!needsCount && (
                    <button type="button" className="bly-needs" onClick={() => goHome({ top: true })} aria-label={`${needsCount} waiting for you. Go to Bailey home`}>
                        <span className="bly-needs-text">Needs you</span><i>{needsCount}</i>
                    </button>
                )}
                <LocationPicker /><span className="bly-bell"><NotificationBell /></span>
                {/* The front desk's pages are places you visit: back is the way out. */}
                {!styling && !page && <MoreMenu items={menuItems} label="More options" />}
            </div>
        </header>
    );

    const thread = (
        <div className="bly-convo">
            <div className="bly-scroll" ref={scrollRef} onScroll={onThreadScroll}>
                <div className="bly-thread">
                    {!shown.length && (
                        <div className="bly-thread-empty">
                            <p>{threadGoalId ? 'Ask how this goal is going, or what to try next.' : 'What would you like to do?'}</p>
                        </div>
                    )}
                    {shown.map((m, i) => {
                        if (m.role === 'me') {
                            return (
                                <div className="bly-msg is-me" key={i}>
                                    <div className="bly-bubble">{m.text}</div>
                                </div>
                            );
                        }
                        const firstOfTurn = i === 0 || shown[i - 1].role === 'me';
                        // Thinking until there are words: before the first step and
                        // between steps (a step in progress says what it's doing)
                        const thinking = m.streaming && !m.text && !(m.steps || []).some(s => !s.done);
                        return (
                            <div className={`bly-msg is-them${firstOfTurn ? ' is-first' : ''}`} key={i}>
                                <div className="bly-face" aria-hidden="true">{firstOfTurn && <BaileyStill appearance={look} face />}</div>
                                <div className="bly-them">
                                    {firstOfTurn && <span className="bly-who">Bailey</span>}
                                    <WorkSteps steps={m.steps} working={m.streaming} />
                                    {thinking && (
                                        <div className="bly-thinking"><span /><span /><span /><em><ThinkingWord /></em></div>
                                    )}
                                    {!!m.text && (
                                        // The model writes markdown; rendering it as plain text
                                        // showed the asterisks. The person's own words stay literal.
                                        // What Bailey shows (a post, an email, the brand) is drawn in place
                                        // A question's answers only work on the latest reply
                                        // A draft card sits after the words, before the closing buttons
                                        <BaileyReply text={m.text} className="bly-md" say={i === lastThem && !m.streaming && !sending ? send : null} asked={askedBefore(shown, i)} drafts={campaignDraftsOf(m.cards)} after={(m.cards || []).map(renderCard(i)).filter((el, ci) => m.cards[ci].type === 'draft')} />
                                    )}
                                    {(m.cards || []).map(renderCard(i)).filter((el, ci) => !m.text || m.cards[ci].type !== 'draft')}
                                    {i === lastThem && !!lastFollow.length && (
                                        <div className="bly-follow" role="group" aria-label="Suggested next steps">
                                            {lastFollow.map(f => (
                                                <button key={f} type="button" className="bly-chip" onClick={() => send(f)}>{f}</button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {error && <div className="bly-error" role="alert">{error}</div>}

            <div className="bly-dock">
                <BaileyQuotaLine bailey={baileyOf(pricing)} isOwner={isOwnerHere()} />
                <Composer
                    variant="dock"
                    value={draft}
                    onChange={setDraft}
                    onSend={() => send()}
                    busy={sending}
                    inputRef={inputRef}
                    onFocus={() => setAttentive(true)}
                    onBlur={() => setAttentive(false)}
                    placeholder={sending ? 'Bailey is working on it…' : 'Reply to Bailey…'}
                />
            </div>
        </div>
    );

    const live = view === 'home' ? liveFor({ sending, hasThread: messages.length > 0, title, petRun, sessionId, petSeenAt }) : null;
    const liveRow = live && {
        ...live,
        go: live.kind === 'here' ? () => setView('thread')
            : live.sessionId ? () => { setPetSeenAt(Date.now()); resume(live.sessionId); } : null
    };

    return (
        // body_container is the app's own page wrapper: it supplies the left
        // offset that clears the fixed side nav and tracks it collapsing.
        <div className="body_container agt-page bly-page">
            <div className={`bly${styling ? ' is-styling' : ''}${showSide ? ' has-side' : ''}`} ref={shellRef} style={shell ? { marginTop: shell.top, height: shell.height } : undefined}>
                {side.dock && !styling && !page && (
                    <ConversationDock
                        {...listProps}
                        open={showSide}
                        onHome={goHome}
                        onNew={() => (sending ? goHome() : newConversation())}
                        onHide={() => setSide('closed')}
                    />
                )}
                {peek.open && (
                    <ConversationPeek
                        {...listProps}
                        onEnter={peek.stay}
                        onLeave={peek.leave}
                        onClose={peek.close}
                        onDock={() => { peek.close(); setSide('open'); }}
                        onHome={goHome}
                        onNew={() => (sending ? goHome() : newConversation())}
                    />
                )}
                <div className="bly-main">
                {header}
                {styling ? (
                    <div className="agt bly-studio">
                        <BaileyWardrobe appearance={look} savedAppearance={savedLook} saving={savingLook} error={lookError} extras={lookExtras.offered}
                            onChange={setLook} onSave={saveLook} onDone={() => { setLookError(null); setStyling(false); }} onClose={closeStyling} />
                    </div>
                ) : page ? pageBody : view === 'thread' ? thread : (
                    <PlaceScroll key="home" place="home" restore={returning.current}>
                        <BaileyQuotaLine bailey={baileyOf(pricing)} isOwner={isOwnerHere()} />
                        {liveRow && (
                            <button type="button" className={`bly-live${liveRow.busy ? '' : ' is-ready'}`} onClick={liveRow.go || undefined} disabled={!liveRow.go}>
                                {liveRow.busy
                                    ? <span className="bly-step-dot" aria-hidden="true" />
                                    : <span className="bly-live-tick" aria-hidden="true"><Icon.Tick size={12} /></span>}
                                <span>{liveRow.text}</span>
                                {liveRow.go && <b>Open</b>}
                            </button>
                        )}
                        <BaileyHome
                            status={status}
                            look={look}
                            baileyState={baileyState}
                            avatarActivity={avatarActivity}
                            motionPaused={motionPaused}
                            canAct={canAct}
                            draft={draft}
                            setDraft={setDraft}
                            onSend={() => send()}
                            sending={sending}
                            inputRef={inputRef}
                            onFocus={() => setAttentive(true)}
                            onBlur={() => setAttentive(false)}
                            waiting={waiting}
                            busyApproval={busyApproval}
                            onApprove={onApprove}
                            onReject={onReject}
                            noticed={noticed}
                            onNoticed={onNoticed}
                            goals={goals}
                            drafts={drafts}
                            pill={pill}
                            onOpenGoal={openGoal}
                            onOpenDraft={openDraft}
                            recent={showSide ? [] : homeRecent}
                            isPinned={isPinned}
                            onResume={resume}
                            onAllConversations={openConversations}
                            ideas={ideas}
                            onIdea={onIdea}
                            frontDesk={fdAllowed ? fd : null}
                            onCall={onCall}
                            onFrontDesk={() => go('/assistant/front-desk')}
                            onFinishSetup={() => go('/assistant/front-desk/setup')}
                            onAllMessages={() => go('/assistant/front-desk/conversations?show=messages')}
                            onStyle={() => setStyling(true)}
                            onSoul={fdAllowed ? () => go('/assistant/soul') : null}
                            history={props.history}
                            salonName={salonName}
                            onHowIWork={() => setPanel('how')}
                        />
                    </PlaceScroll>
                )}
                </div>
            </div>
            {!side.dock && drawerOpen && !styling && (
                <ConversationDrawer
                    {...listProps}
                    returnFocus={toggleRef}
                    onHome={goHome}
                    onNew={() => (sending ? goHome() : newConversation())}
                    onClose={() => setDrawerOpen(false)}
                />
            )}
            {panel === 'how' && <HowIWorkPanel status={status} frontDesk={fdAllowed && fd.on} onClose={() => setPanel(null)} />}
        </div>
    );
}
